import debounce from "./debounce";
// Conditionally import StickyHeader only in browser (not SSR).
// StickyHeader has module-level document access, so it can't be imported in Node.js.
const NoopStickyHeader = {
    data: { stickyHeaderElement: null },
    utils: {
        isHeaderSticky: () => false,
        getStickyHeaderHeight: () => 0,
    },
};
const StickyHeader = typeof document !== "undefined"
    ? require("../components/StickyHeader").default
    : NoopStickyHeader;
function getAnchorFromLink(link) {
    return link.getAttribute("data-target") || link.getAttribute("href");
}
/**
 * Get target element from link URL or target ID.
 */
function getTargetFromHashLink(link) {
    const anchor = getAnchorFromLink(link);
    const el = document.getElementById(anchor.split("#")[1]);
    return el ? el : null;
}
/**
 * Scroll to specific element.
 */
export function scrollToElement({ target, behavior = "smooth", disableDebounce = false, marginTop, }) {
    if (!target)
        return;
    const scrollToElementEvent = new Event("to-element.restaumatic.scroll", {
        bubbles: false,
        cancelable: true,
        composed: true,
    });
    document.dispatchEvent(scrollToElementEvent);
    // Getting margin top value from sticky elements: sticky-menu (Menu page sections) and sticky-header (page navbar) that can cover the target element. It can be combined if both are present, eg. on the one-page sites.
    const getHeightOfStickyElements = (target) => {
        let height = 0;
        if (StickyHeader.data.stickyHeaderElement &&
            StickyHeader.utils.isHeaderSticky()) {
            height += StickyHeader.utils.getStickyHeaderHeight();
        }
        // NOTICE: StickyTabs experiment, needs refactor
        const stickyTabsEl = document.querySelector(".m-flex-layout__aside--sticky-tabs");
        if (stickyTabsEl && /^menu/.test(target.id)) {
            height += stickyTabsEl.clientHeight;
        }
        if (StickyHeader.data.stickyHeaderElement || stickyTabsEl) {
            return height;
        }
        return null;
    };
    // TODO: Refactor this function to eliminate the "scrollMarginTop" logic
    const handleResizeAndScroll = debounce(() => {
        const scrollMarginTop = marginTop !== null && marginTop !== void 0 ? marginTop : getHeightOfStickyElements(target);
        if (scrollMarginTop !== null) {
            target.style.scrollMarginTop = `${scrollMarginTop}px`;
        }
        // Perform scroll
        requestAnimationFrame(() => {
            target.scrollIntoView({
                behavior,
                block: "start",
            });
        });
    }, disableDebounce ? 0 : 100);
    const resizeObserver = new ResizeObserver(handleResizeAndScroll);
    // Observe body to detect layout shifts due to loading images, showing sections dynamically etc.
    resizeObserver.observe(document.body);
    // Observe sticky header due to its height change and applying different scrollMarginTop to the target element
    const { stickyHeaderElement } = StickyHeader.data;
    if (stickyHeaderElement) {
        resizeObserver.observe(stickyHeaderElement);
    }
    // Clean up after reasonable time
    setTimeout(() => resizeObserver.disconnect(), 1000);
}
/**
 * Scroll to element if target is above our position (it's very likely that user expects it).
 * @param target The element to check and potentially scroll to
 * @param visibleThreshold Number of pixels from the top where element is still considered not visible (default: 0)
 * @param marginTop Optional fixed scroll margin top in pixels (overrides computed value)
 */
export function scrollIfAbove({ target, visibleThreshold = 0, marginTop, }) {
    if (!target) {
        return false;
    }
    const rect = target.getBoundingClientRect();
    const offsetTop = rect.top;
    // Adjust distance by sticky header height
    const distanceTop = Math.round(offsetTop - StickyHeader.utils.getStickyHeaderHeight());
    // Scroll if:
    // 1. Element is above viewport (negative distance - completely out of view)
    // 2. OR element's top is less than the threshold pixels from viewport top (partially visible)
    if (distanceTop < visibleThreshold) {
        scrollToElement({ target, marginTop });
        return true;
    }
    return false;
}
/**
 * Scroll to element if target is below our position.
 * @param target The element to check and potentially scroll to
 * @param visibleThreshold Number of pixels from the bottom where element is still considered not visible (default: 0)
 * @param marginTop Optional fixed scroll margin top in pixels (overrides computed value)
 */
export function scrollIfBelow({ target, visibleThreshold = 0, marginTop, }) {
    if (!target) {
        return false;
    }
    const rect = target.getBoundingClientRect();
    // Calculate if element is below viewport
    const isElementBelowViewport = rect.top > window.innerHeight;
    // Determine if the element is partially visible near the bottom edge of the viewport
    // The element is considered partially visible if less than visibleThreshold pixels are showing
    const isPartiallyVisible = window.innerHeight - rect.top < visibleThreshold;
    if (isElementBelowViewport || isPartiallyVisible) {
        scrollToElement({ target, marginTop });
        return true;
    }
    return false;
}
/**
 * Scroll to element if target is not visible (below or above our position).
 * @param target The element to check and potentially scroll to
 * @param topThreshold Number of pixels from the top where element is still considered not visible (default: 0)
 * @param bottomThreshold Number of pixels from the bottom where element is still considered not visible (default: 0)
 * @param marginTop Optional fixed scroll margin top in pixels (overrides computed value)
 */
export function scrollIfNotVisible({ target, topThreshold = 0, bottomThreshold = 0, marginTop, }) {
    // Check if target element is within a sticky parent (eg. sticky dock or footer) - if so, don't scroll
    if (target.parentElement !== null) {
        const parentPosition = window.getComputedStyle(target.parentElement).position;
        if (parentPosition === "sticky" || parentPosition === "fixed") {
            return false;
        }
    }
    return (scrollIfAbove({ target, visibleThreshold: topThreshold, marginTop }) ||
        scrollIfBelow({ target, visibleThreshold: bottomThreshold, marginTop }));
}
/**
 * Init scroll utility for specific anchor links.
 */
export function initScroll(anchorPrefix = "section-") {
    const anchors = document.querySelectorAll(`a[href*="#${anchorPrefix}"]`);
    anchors.forEach((item) => {
        item.addEventListener("click", (e) => {
            const link = e.currentTarget;
            const target = getTargetFromHashLink(link);
            if (!target)
                return;
            history.pushState(null, "", getAnchorFromLink(link));
            scrollToElement({ target });
            e.preventDefault();
        });
    });
}
/**
 * Scroll to the first error element within the parent element if not visible or flash it if visible.
 *
 * @param parentSelector Selector of the element to search within
 * @param errorSelectors Search for these elements within the parent (the order matters)
 */
export function scrollToError(parentSelector, errorSelectors) {
    // Get parent element
    const parentElement = document.querySelector(parentSelector);
    if (!parentElement) {
        return;
    }
    // Wait for errors render
    setTimeout(() => {
        // Get first visible error element
        const errorElement = errorSelectors
            .map((selector) => Array.from(parentElement.querySelectorAll(selector)))
            .flat()
            .filter((el) => {
            if (el && "checkVisibility" in el) {
                // @ts-ignore - `checkVisibility()` is modern method that is not yet included in TypeScript's default HTMLElement type definitions
                return el.checkVisibility({ checkVisibilityCSS: true });
            }
            return false;
        })
            .shift();
        if (!errorElement) {
            return;
        }
        // Try to scroll if element is not "visible"
        const scrolled = scrollIfNotVisible({
            target: errorElement,
            topThreshold: 80, // consider element not visible because of sticky header
            bottomThreshold: 180, // consider element not visible because of sticky footer or visible only small part of it
            marginTop: 160, // add some extra margin to make sure the element is not hidden behind sticky header, scroll to the region of the middle of the screen to be better accessible by thumb on mobile
        });
        // "Flash" the element if it's already visible
        if (!scrolled) {
            const root = window.document.querySelector(":root");
            if (!root) {
                return;
            }
            const themeBaseDuration = getComputedStyle(root).getPropertyValue("--base-duration") || "0.3"; // in s
            const animationDuration = parseFloat(themeBaseDuration) * 1000; // in ms
            errorElement.style.animation = `fadeIn ${animationDuration}ms`;
            setTimeout(() => {
                errorElement.style.animation = "";
            }, animationDuration);
        }
    }, 200);
}
